// i18n fragment - populated by the Phase 6 sweep. See ../strings.ts.
import type { LocaleStrings } from './base';

export const schedulesNotifStrings: LocaleStrings = {
  en: {
    // ── web-ui SchedulesView.vue ──
    'schedules.title':             'Your schedules',
    'schedules.new':               'New schedule',
    'schedules.this_schedule':     'this schedule',
    'schedules.cancel.title':      'Cancel schedule?',
    'schedules.cancel.body':       '"{name}" will be archived. Past runs stay in your history.',
    'schedules.cancel.confirm':    'Cancel schedule',
    'schedules.cancel.keep':       'Keep it',
    'schedules.erase.title':       'Erase permanently?',
    'schedules.erase.body':        '"{name}" will be deleted forever. Past run records survive but will no longer reference this schedule. This cannot be undone.',
    'schedules.erase.confirm':     'Erase permanently',
    'schedules.erase.keep':        'Keep archived',
    'schedules.erase.failed':      'Erase failed',
    'schedules.filter_aria':       'Schedule filter',
    'schedules.tab_active':        'Active',
    'schedules.tab_all':           'All',
    'schedules.empty_title':       'No schedules yet',
    'schedules.empty_body':        'Try saying to HQ: "every morning at 9, send me yesterday\'s revenue." Or set one up here.',

    // ── web-ui ScheduleEditorView.vue ──
    'scheduleEditor.title_edit':           'Edit schedule',
    'scheduleEditor.title_new':            'New schedule',
    'scheduleEditor.save_changes':         'Save changes',
    'scheduleEditor.create':               'Create schedule',
    'scheduleEditor.usage.title':          'Consumption',
    'scheduleEditor.usage.lede':           'Token and cost this schedule has used, across its runs.',
    'scheduleEditor.usage.loading':        'Loading usage...',
    'scheduleEditor.usage.total_cost':     'Total cost',
    'scheduleEditor.usage.avg_per_run':    'Avg / run',
    'scheduleEditor.usage.tokens':         'Total tokens',
    'scheduleEditor.usage.runs':           'Runs',
    'scheduleEditor.usage.spark_cap':      'Cost per run',
    'scheduleEditor.usage.spark_aria':     'Cost per run over time',
    'scheduleEditor.usage.empty':          'No runs yet. Usage will appear here after this schedule fires.',
    'scheduleEditor.task.title':           'Task',
    'scheduleEditor.task.lede':            'What HQ runs each time, and which agent runs it.',
    'scheduleEditor.name_ph':              'Monday #general digest',
    'scheduleEditor.optional':             'Optional',
    'scheduleEditor.description_ph':       'Weekly summary of the most active conversations',
    'scheduleEditor.agent_label':          'Agent',
    'scheduleEditor.agent_hint':           'Which agent runs the schedule.',
    'scheduleEditor.prompt_label':         'Prompt',
    'scheduleEditor.prompt_hint':          'The instructions HQ runs every time this schedule fires.',
    'scheduleEditor.prompt_ph':            'Summarise the most active conversations in #general over the last week, 3-5 bullet points.',
    'scheduleEditor.cadence.title':        'Cadence',
    'scheduleEditor.cadence.lede':         'When this schedule fires.',
    'scheduleEditor.delivery.title':       'Delivery',
    'scheduleEditor.delivery.lede':        'Where the result is sent when it runs.',
    'scheduleEditor.deliver_to':           'Deliver to',
    'scheduleEditor.deliver.slack_dm_me':    'Slack DM (to me)',
    'scheduleEditor.deliver.slack_dm_owner': 'Slack DM (to owner)',
    'scheduleEditor.deliver.slack_channel':  'Slack channel',
    'scheduleEditor.deliver.teams_channel':  'Teams channel',
    'scheduleEditor.deliver.teams_dm':       'Teams chat',
    'scheduleEditor.deliver.teams_dm_me':    'Teams DM (to me)',
    'scheduleEditor.deliver.web_inbox':      'Web inbox',
    'scheduleEditor.dm.you_named':         '{name} (you)',
    'scheduleEditor.dm.you':               'you',
    'scheduleEditor.dm.owner':             'the schedule owner',
    'scheduleEditor.delivered_to_before':  'Delivered to',
    'scheduleEditor.slack_handle':         'Slack {id}',
    'scheduleEditor.channel_label':        'Channel',
    'scheduleEditor.channel_hint':         'Only channels HQ has been added to are shown.',
    'scheduleEditor.channel_ph':           'Choose a channel…',
    'scheduleEditor.channel_private':      'private',
    'scheduleEditor.channels_loading':     'Loading channels…',
    'scheduleEditor.slack_not_connected':  "HQ isn't connected to Slack in this workspace.",
    'scheduleEditor.no_channels':          "HQ isn't in any channels yet - invite it to a channel, then reopen this.",
    'scheduleEditor.teams_channel_hint':   'Only standard channels in teams HQ is installed in are shown.',
    'scheduleEditor.teams_not_connected':  "HQ isn't connected to Teams in this workspace.",
    'scheduleEditor.teams_no_channels':    "HQ isn't in any Teams channels yet - add it to a team, then reopen this.",
    'scheduleEditor.teams_dm_note':        'Delivered to the Teams chat this schedule was created in.',
    'scheduleEditor.teams_dm_me_note':     'Delivered to your 1:1 Teams chat with HQ. Needs HQ added to your personal Teams apps.',
    'scheduleEditor.fresh_each_run':       'Fresh conversation each run',
    'scheduleEditor.fresh_hint':           '- on starts each run clean; off (default) lets HQ build on earlier runs.',
    'scheduleEditor.max_steps_label':      'Max steps per run',
    'scheduleEditor.max_steps_hint':       'How many tool-call rounds the agent may take each run. Default 200; raise it for heavy multi-step work (research, multi-site browsing), lower it to keep a simple digest cheap. Range 1-1000.',
    'scheduleEditor.max_duration_label':   'Max runtime (minutes)',
    'scheduleEditor.max_duration_hint':    'Wall-clock cap per run. A run that goes past it is stopped and marked failed. Default 30 min; raise it alongside the step budget for heavy schedules so they have time to finish. Range 1-60.',
    'scheduleEditor.toast.not_found':      'That schedule no longer exists.',
    'scheduleEditor.toast.saved':          'Saved schedule changes',
    'scheduleEditor.toast.created':        'Created schedule "{name}"',
    'scheduleEditor.err.save':             'Failed to save changes',
    'scheduleEditor.err.create':           'Failed to create schedule',
    // Live review block at the foot of the editor.
    'scheduleEditor.summary.sentence':     '{cadence}, {agent} runs this task and sends the result to {target}.',
    'scheduleEditor.summary.incomplete':   'To save, add {missing}.',
    'scheduleEditor.summary.need_name':    'a name',
    'scheduleEditor.summary.need_task':    'a task',
    'scheduleEditor.summary.need_agent':   'an agent',
    'scheduleEditor.summary.need_cadence': 'a valid cadence',
    'scheduleEditor.summary.need_channel': 'a channel',
    'scheduleEditor.summary.tgt_teams_dm': 'a Microsoft Teams chat',
    'scheduleEditor.summary.tgt_teams_dm_me': 'your Teams chat',
    'scheduleEditor.summary.tgt_web_inbox':'your web inbox',

    // ── web-ui NotificationsView.vue / NotificationBell.vue / NotificationItem.vue ──
    'notifications.title':            'Notifications',
    'notifications.subtitle':         'Updates from HQ - onboarding steps, integrations, and workspace activity.',
    'notifications.mark_all_read':    'Mark all read',
    'notifications.empty_title':      "You're all caught up",
    'notifications.empty_body_view':  'New notifications will show up here as you set up your workspace.',
    'notifications.empty_body_bell':  'New notifications will show up here.',
    'notifications.view_all':         'View all',
    'notifications.cta_open':         'Open',
    'notifications.dismiss':          'Dismiss',

    // ── web-ui ScheduleCard.vue ──
    'scheduleCard.unknown_owner':         'Unknown',
    'scheduleCard.state.active':          'Active',
    'scheduleCard.state.paused':          'Paused',
    'scheduleCard.state.archived':        'Archived',
    'scheduleCard.owner_tip':             'Schedule owner',
    'scheduleCard.deliver.slack_dm':      'Slack DM',
    'scheduleCard.deliver.slack_channel': 'Slack channel',
    'scheduleCard.deliver.web_inbox':     'Web inbox',
    'scheduleCard.deliver.teams_dm':      'Teams DM',
    'scheduleCard.deliver.teams_channel': 'Teams channel',
    'scheduleCard.agent':                 'Agent',
    'scheduleCard.deliver_to':            'Deliver to',
    'scheduleCard.last_run':              'Last run',
    'scheduleCard.lag':                   'lag {value}',
    'scheduleCard.lag_tip':               'Time between scheduled fire and body start',
    'scheduleCard.next_run':              'Next run',
    'scheduleCard.next_short':            'next {when}',
    'scheduleCard.fails_short':           '{count} failed',
    'scheduleCard.failures':              'Failures',
    'scheduleCard.failures_count':        '{count} consecutive (auto-pause at 5)',
    'scheduleCard.action.last_run':       'View last run',
    'scheduleCard.action.last_run_tip':   'View the latest run (read-only)',
    'scheduleCard.action.history':        'Run history',
    'scheduleCard.action.history_tip':    'See every run of this schedule',
    'scheduleCard.action.more':           'More actions',
    'scheduleCard.action.fire':           'Fire now',
    'scheduleCard.action.fire_tip':       'Run this schedule now (one-shot - normal cadence continues)',
    'scheduleCard.action.pause':          'Pause',
    'scheduleCard.action.resume':         'Resume',
    'scheduleCard.action.cancel':         'Cancel schedule',
    'scheduleCard.action.cancel_tip':     'Cancel - archives the schedule',
    'scheduleCard.action.erase':          'Erase permanently',
    'scheduleCard.action.erase_tip':      'Erase permanently - deletes forever',

    // ── web-ui CadencePicker.vue ──
    'cadence.preset.daily':        'Daily',
    'cadence.preset.weekdays':     'Weekdays',
    'cadence.preset.weekly':       'Weekly',
    'cadence.preset.monthly':      'Monthly',
    'cadence.preset.once':         'Once',
    'cadence.preset.custom':       'Custom',
    'cadence.weekday.mon':         'Monday',
    'cadence.weekday.tue':         'Tuesday',
    'cadence.weekday.wed':         'Wednesday',
    'cadence.weekday.thu':         'Thursday',
    'cadence.weekday.fri':         'Friday',
    'cadence.weekday.sat':         'Saturday',
    'cadence.weekday.sun':         'Sunday',
    // Short labels for the weekly day-toggle pills.
    'cadence.weekday_short.mon':   'Mon',
    'cadence.weekday_short.tue':   'Tue',
    'cadence.weekday_short.wed':   'Wed',
    'cadence.weekday_short.thu':   'Thu',
    'cadence.weekday_short.fri':   'Fri',
    'cadence.weekday_short.sat':   'Sat',
    'cadence.weekday_short.sun':   'Sun',
    'cadence.skips_short_months':  'skips short months',
    'cadence.custom_expr_summary': 'Custom cron expression',
    'cadence.repeats':             'Repeats',
    'cadence.on':                  'On',
    'cadence.weekday_aria':        'Weekday',
    'cadence.day_of_month':        'Day of month',
    'cadence.on_date':             'On',
    'cadence.month_aria':          'Month',
    'cadence.day_aria':            'Day',
    'cadence.year_aria':           'Year',
    'cadence.once_past':           'That date and time is in the past.',
    'cadence.at':                  'At',
    'cadence.hour_aria':           'Hour',
    'cadence.minute_aria':         'Minute',
    'cadence.timezone':            'Timezone',
    'cadence.cron_expr':           'Cron expression',
    'cadence.cron_fields':         '- minute hour day month weekday',
    'cadence.invalid_cron':        "That isn't a valid cron expression - it needs 5 fields.",
    'cadence.when':                'When',

    // ── FileReferencePicker.vue (composer + schedule editor) ──
    'filePicker.title':            'Reference a file',
    'filePicker.search_ph':        'Search by name...',
    'filePicker.src.documents':    'Documents',
    'filePicker.src.artifacts':    'Generated',
    'filePicker.src.conversation': 'This conversation',
    'filePicker.src.work':         'Workspace files',
    'filePicker.use_folder':       'Reference this folder',
    'filePicker.root':             'All documents',
    'filePicker.empty':            'Nothing here',
    'filePicker.docs_count':       '{n} files',
    'filePicker.work_hint':        'Files under /work exist only during this run - they are not durable.',
    'filePicker.load_error':       'Could not load files',
    'filePicker.add':              'Add references',
    'filePicker.add_n':            'Add {n}',

    // ── ScheduleEditorView.vue - referenced files ──
    'scheduleEditor.refs.title':   'Files',
    'scheduleEditor.refs.lede':    'Pin files this schedule should always work from. They are staged in before every run; a folder re-resolves to its current contents each time.',
    'scheduleEditor.refs.add':     'Add a file or folder',
    'scheduleEditor.refs.empty':   'No files referenced.',
    'scheduleEditor.refs.folder':  'Folder',
    'scheduleEditor.refs.remove':  'Remove',
  },
  sv: {
    // ── web-ui SchedulesView.vue ──
    'schedules.title':             'Dina scheman',
    'schedules.new':               'Nytt schema',
    'schedules.this_schedule':     'det här schemat',
    'schedules.cancel.title':      'Avbryta schemat?',
    'schedules.cancel.body':       '"{name}" arkiveras. Tidigare körningar finns kvar i din historik.',
    'schedules.cancel.confirm':    'Avbryt schemat',
    'schedules.cancel.keep':       'Behåll det',
    'schedules.erase.title':       'Radera permanent?',
    'schedules.erase.body':        '"{name}" tas bort för alltid. Tidigare körningsposter finns kvar men refererar inte längre till det här schemat. Detta kan inte ångras.',
    'schedules.erase.confirm':     'Radera permanent',
    'schedules.erase.keep':        'Behåll arkiverat',
    'schedules.erase.failed':      'Raderingen misslyckades',
    'schedules.filter_aria':       'Schemafilter',
    'schedules.tab_active':        'Aktiva',
    'schedules.tab_all':           'Alla',
    'schedules.empty_title':       'Inga scheman än',
    'schedules.empty_body':        'Prova att säga till HQ: "varje morgon klockan 9, skicka mig gårdagens intäkter." Eller sätt upp ett här.',

    // ── web-ui ScheduleEditorView.vue ──
    'scheduleEditor.title_edit':           'Redigera schema',
    'scheduleEditor.title_new':            'Nytt schema',
    'scheduleEditor.save_changes':         'Spara ändringar',
    'scheduleEditor.create':               'Skapa schema',
    'scheduleEditor.usage.title':          'Förbrukning',
    'scheduleEditor.usage.lede':           'Tokens och kostnad som schemat har använt, över sina körningar.',
    'scheduleEditor.usage.loading':        'Laddar förbrukning...',
    'scheduleEditor.usage.total_cost':     'Total kostnad',
    'scheduleEditor.usage.avg_per_run':    'Snitt / körning',
    'scheduleEditor.usage.tokens':         'Totala tokens',
    'scheduleEditor.usage.runs':           'Körningar',
    'scheduleEditor.usage.spark_cap':      'Kostnad per körning',
    'scheduleEditor.usage.spark_aria':     'Kostnad per körning över tid',
    'scheduleEditor.usage.empty':          'Inga körningar än. Förbrukning visas här när schemat har körts.',
    'scheduleEditor.task.title':           'Uppgift',
    'scheduleEditor.task.lede':            'Vad HQ kör varje gång, och vilken agent som kör det.',
    'scheduleEditor.name_ph':              'Måndagens #general-sammandrag',
    'scheduleEditor.optional':             'Valfritt',
    'scheduleEditor.description_ph':       'Veckosammanfattning av de mest aktiva konversationerna',
    'scheduleEditor.agent_label':          'Agent',
    'scheduleEditor.agent_hint':           'Vilken agent som kör schemat.',
    'scheduleEditor.prompt_label':         'Prompt',
    'scheduleEditor.prompt_hint':          'Instruktionerna HQ kör varje gång det här schemat utlöses.',
    'scheduleEditor.prompt_ph':            'Sammanfatta de mest aktiva konversationerna i #general den senaste veckan, 3-5 punkter.',
    'scheduleEditor.cadence.title':        'Kadens',
    'scheduleEditor.cadence.lede':         'När det här schemat utlöses.',
    'scheduleEditor.delivery.title':       'Leverans',
    'scheduleEditor.delivery.lede':        'Vart resultatet skickas när det körs.',
    'scheduleEditor.deliver_to':           'Leverera till',
    'scheduleEditor.deliver.slack_dm_me':    'Slack-DM (till mig)',
    'scheduleEditor.deliver.slack_dm_owner': 'Slack-DM (till ägaren)',
    'scheduleEditor.deliver.slack_channel':  'Slack-kanal',
    'scheduleEditor.deliver.teams_channel':  'Teams-kanal',
    'scheduleEditor.deliver.teams_dm':       'Teams-chatt',
    'scheduleEditor.deliver.teams_dm_me':    'Teams-DM (till mig)',
    'scheduleEditor.deliver.web_inbox':      'Webbinkorg',
    'scheduleEditor.dm.you_named':         '{name} (du)',
    'scheduleEditor.dm.you':               'dig',
    'scheduleEditor.dm.owner':             'schemats ägare',
    'scheduleEditor.delivered_to_before':  'Levereras till',
    'scheduleEditor.slack_handle':         'Slack {id}',
    'scheduleEditor.channel_label':        'Kanal',
    'scheduleEditor.channel_hint':         'Endast kanaler som HQ har lagts till i visas.',
    'scheduleEditor.channel_ph':           'Välj en kanal…',
    'scheduleEditor.channel_private':      'privat',
    'scheduleEditor.channels_loading':     'Läser in kanaler…',
    'scheduleEditor.slack_not_connected':  'HQ är inte ansluten till Slack i den här arbetsytan.',
    'scheduleEditor.no_channels':          'HQ är inte med i några kanaler än - bjud in den till en kanal och öppna sedan det här igen.',
    'scheduleEditor.teams_channel_hint':   'Endast standardkanaler i team där HQ är installerat visas.',
    'scheduleEditor.teams_not_connected':  'HQ är inte anslutet till Teams i den här arbetsytan.',
    'scheduleEditor.teams_no_channels':    'HQ är inte med i några Teams-kanaler än - lägg till det i ett team och öppna sedan det här igen.',
    'scheduleEditor.teams_dm_note':        'Levereras till Teams-chatten där schemat skapades.',
    'scheduleEditor.teams_dm_me_note':     'Levereras till din 1:1-chatt med HQ i Teams. Kräver att HQ är tillagt bland dina personliga Teams-appar.',
    'scheduleEditor.fresh_each_run':       'Ny konversation varje körning',
    'scheduleEditor.fresh_hint':           '- på börjar varje körning rent; av (standard) låter HQ bygga på tidigare körningar.',
    'scheduleEditor.max_steps_label':      'Max steg per körning',
    'scheduleEditor.max_steps_hint':       'Hur många verktygsanrop-rundor agenten får ta varje körning. Standard 200; höj för tunga flerstegsuppgifter (research, surfa flera sajter), sänk för att hålla en enkel sammanfattning billig. Intervall 1-1000.',
    'scheduleEditor.max_duration_label':   'Max körtid (minuter)',
    'scheduleEditor.max_duration_hint':    'Tidsgräns per körning. En körning som passerar den stoppas och markeras som misslyckad. Standard 30 min; höj tillsammans med stegbudgeten för tunga scheman så de hinner bli klara. Intervall 1-60.',
    'scheduleEditor.toast.not_found':      'Det schemat finns inte längre.',
    'scheduleEditor.toast.saved':          'Sparade schemaändringar',
    'scheduleEditor.toast.created':        'Skapade schemat "{name}"',
    'scheduleEditor.err.save':             'Det gick inte att spara ändringarna',
    'scheduleEditor.err.create':           'Det gick inte att skapa schemat',
    // Live granskningsruta längst ner i redigeraren.
    'scheduleEditor.summary.sentence':     '{cadence} kör {agent} den här uppgiften och skickar resultatet till {target}.',
    'scheduleEditor.summary.incomplete':   'Lägg till {missing} för att spara.',
    'scheduleEditor.summary.need_name':    'ett namn',
    'scheduleEditor.summary.need_task':    'en uppgift',
    'scheduleEditor.summary.need_agent':   'en agent',
    'scheduleEditor.summary.need_cadence': 'en giltig kadens',
    'scheduleEditor.summary.need_channel': 'en kanal',
    'scheduleEditor.summary.tgt_teams_dm': 'en Microsoft Teams-chatt',
    'scheduleEditor.summary.tgt_teams_dm_me': 'din Teams-chatt',
    'scheduleEditor.summary.tgt_web_inbox':'din webbinkorg',

    // ── web-ui NotificationsView.vue / NotificationBell.vue / NotificationItem.vue ──
    'notifications.title':            'Aviseringar',
    'notifications.subtitle':         'Uppdateringar från HQ - introduktionssteg, integrationer och aktivitet i arbetsytan.',
    'notifications.mark_all_read':    'Markera alla som lästa',
    'notifications.empty_title':      'Du är ikapp',
    'notifications.empty_body_view':  'Nya aviseringar visas här när du sätter upp din arbetsyta.',
    'notifications.empty_body_bell':  'Nya aviseringar visas här.',
    'notifications.view_all':         'Visa alla',
    'notifications.cta_open':         'Öppna',
    'notifications.dismiss':          'Avfärda',

    // ── web-ui ScheduleCard.vue ──
    'scheduleCard.unknown_owner':         'Okänd',
    'scheduleCard.state.active':          'Aktivt',
    'scheduleCard.state.paused':          'Pausat',
    'scheduleCard.state.archived':        'Arkiverat',
    'scheduleCard.owner_tip':             'Schemats ägare',
    'scheduleCard.deliver.slack_dm':      'Slack-DM',
    'scheduleCard.deliver.slack_channel': 'Slack-kanal',
    'scheduleCard.deliver.web_inbox':     'Webbinkorg',
    'scheduleCard.deliver.teams_dm':      'Teams-DM',
    'scheduleCard.deliver.teams_channel': 'Teams-kanal',
    'scheduleCard.agent':                 'Agent',
    'scheduleCard.deliver_to':            'Leverera till',
    'scheduleCard.last_run':              'Senaste körning',
    'scheduleCard.lag':                   'fördröjning {value}',
    'scheduleCard.lag_tip':               'Tid mellan schemalagd utlösning och kroppsstart',
    'scheduleCard.next_run':              'Nästa körning',
    'scheduleCard.next_short':            'nästa {when}',
    'scheduleCard.fails_short':           '{count} misslyckade',
    'scheduleCard.failures':              'Fel',
    'scheduleCard.failures_count':        '{count} i rad (pausas automatiskt vid 5)',
    'scheduleCard.action.last_run':       'Visa senaste körning',
    'scheduleCard.action.last_run_tip':   'Visa den senaste körningen (skrivskyddad)',
    'scheduleCard.action.history':        'Körhistorik',
    'scheduleCard.action.history_tip':    'Se alla körningar av det här schemat',
    'scheduleCard.action.more':           'Fler åtgärder',
    'scheduleCard.action.fire':           'Kör nu',
    'scheduleCard.action.fire_tip':       'Kör det här schemat nu (en gång - den normala kadensen fortsätter)',
    'scheduleCard.action.pause':          'Pausa',
    'scheduleCard.action.resume':         'Återuppta',
    'scheduleCard.action.cancel':         'Avbryt schemat',
    'scheduleCard.action.cancel_tip':     'Avbryt - arkiverar schemat',
    'scheduleCard.action.erase':          'Radera permanent',
    'scheduleCard.action.erase_tip':      'Radera permanent - tas bort för alltid',

    // ── web-ui CadencePicker.vue ──
    'cadence.preset.daily':        'Dagligen',
    'cadence.preset.weekdays':     'Vardagar',
    'cadence.preset.weekly':       'Veckovis',
    'cadence.preset.monthly':      'Månadsvis',
    'cadence.preset.once':         'En gång',
    'cadence.preset.custom':       'Anpassad',
    'cadence.weekday.mon':         'Måndag',
    'cadence.weekday.tue':         'Tisdag',
    'cadence.weekday.wed':         'Onsdag',
    'cadence.weekday.thu':         'Torsdag',
    'cadence.weekday.fri':         'Fredag',
    'cadence.weekday.sat':         'Lördag',
    'cadence.weekday.sun':         'Söndag',
    // Korta etiketter för veckodags-pillen.
    'cadence.weekday_short.mon':   'Mån',
    'cadence.weekday_short.tue':   'Tis',
    'cadence.weekday_short.wed':   'Ons',
    'cadence.weekday_short.thu':   'Tor',
    'cadence.weekday_short.fri':   'Fre',
    'cadence.weekday_short.sat':   'Lör',
    'cadence.weekday_short.sun':   'Sön',
    'cadence.skips_short_months':  'hoppar över korta månader',
    'cadence.custom_expr_summary': 'Anpassat cron-uttryck',
    'cadence.repeats':             'Upprepas',
    'cadence.on':                  'På',
    'cadence.weekday_aria':        'Veckodag',
    'cadence.day_of_month':        'Dag i månaden',
    'cadence.on_date':             'Den',
    'cadence.month_aria':          'Månad',
    'cadence.day_aria':            'Dag',
    'cadence.year_aria':           'År',
    'cadence.once_past':           'Det datumet och klockslaget har redan passerat.',
    'cadence.at':                  'Klockan',
    'cadence.hour_aria':           'Timme',
    'cadence.minute_aria':         'Minut',
    'cadence.timezone':            'Tidszon',
    'cadence.cron_expr':           'Cron-uttryck',
    'cadence.cron_fields':         '- minut timme dag månad veckodag',
    'cadence.invalid_cron':        'Det är inte ett giltigt cron-uttryck - det behöver 5 fält.',
    'cadence.when':                'När',

    // ── FileReferencePicker.vue (composer + schedule editor) ──
    'filePicker.title':            'Referera en fil',
    'filePicker.search_ph':        'Sök på namn...',
    'filePicker.src.documents':    'Dokument',
    'filePicker.src.artifacts':    'Genererade',
    'filePicker.src.conversation': 'Den här konversationen',
    'filePicker.src.work':         'Arbetsytans filer',
    'filePicker.use_folder':       'Referera den här mappen',
    'filePicker.root':             'Alla dokument',
    'filePicker.empty':            'Inget här',
    'filePicker.docs_count':       '{n} filer',
    'filePicker.work_hint':        'Filer under /work finns bara under den här körningen - de är inte beständiga.',
    'filePicker.load_error':       'Kunde inte läsa in filer',
    'filePicker.add':              'Lägg till referenser',
    'filePicker.add_n':            'Lägg till {n}',

    // ── ScheduleEditorView.vue - refererade filer ──
    'scheduleEditor.refs.title':   'Filer',
    'scheduleEditor.refs.lede':    'Fäst filer som schemat alltid ska utgå från. De läggs in före varje körning; en mapp läses om till sitt aktuella innehåll varje gång.',
    'scheduleEditor.refs.add':     'Lägg till en fil eller mapp',
    'scheduleEditor.refs.empty':   'Inga filer refererade.',
    'scheduleEditor.refs.folder':  'Mapp',
    'scheduleEditor.refs.remove':  'Ta bort',
  },
};
